'use client';

import { useState, useEffect, useRef } from 'react';
import { useReveal } from '@/hooks/use-reveal';

interface Gesture {
  num: string;
  title: string;
  duration: string;
  text: string;
  svg: JSX.Element;
}

const GESTURES: Gesture[] = [
  {
    num: 'I',
    title: 'La Sélection',
    duration: 'Jour 1',
    text: "Chaque peau est examinée à la loupe, à la lumière naturelle du jour. Nous ne retenons que les cuirs pleine fleur au grain serré, à la main franche, sans la moindre imperfection. Une peau sur trois seulement traverse le seuil de l'atelier.",
    svg: (
      <svg viewBox="0 0 120 120" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.2">
        <circle cx="50" cy="50" r="28" />
        <line x1="70" y1="70" x2="92" y2="92" strokeWidth="2.5" />
        <path d="M 35 45 Q 50 38 65 45 Q 70 55 65 60 Q 50 65 35 60 Q 30 55 35 45 Z" strokeWidth="0.8" opacity="0.7" />
        <circle cx="44" cy="50" r="0.8" fill="currentColor" stroke="none" />
        <circle cx="52" cy="48" r="0.8" fill="currentColor" stroke="none" />
        <circle cx="58" cy="52" r="0.8" fill="currentColor" stroke="none" />
        <circle cx="48" cy="55" r="0.8" fill="currentColor" stroke="none" />
        <circle cx="56" cy="58" r="0.8" fill="currentColor" stroke="none" />
        <circle cx="50" cy="50" r="28" strokeDasharray="3 4" className="gesture-shimmer" />
      </svg>
    ),
  },
  {
    num: 'II',
    title: 'Le Tracé',
    duration: 'Jour 2',
    text: "Le patron est tracé à la main, au crayon gras, directement sur le cuir. L'artisan suit la courbe naturelle de la peau pour épargner les zones fragiles. Chaque ligne dictée par des décennies de pratique, jamais par une machine.",
    svg: (
      <svg viewBox="0 0 120 120" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.2">
        <rect x="20" y="25" width="80" height="70" strokeWidth="0.8" opacity="0.5" />
        <line x1="20" y1="40" x2="100" y2="40" strokeWidth="0.4" opacity="0.3" />
        <line x1="20" y1="55" x2="100" y2="55" strokeWidth="0.4" opacity="0.3" />
        <line x1="20" y1="70" x2="100" y2="70" strokeWidth="0.4" opacity="0.3" />
        <line x1="20" y1="85" x2="100" y2="85" strokeWidth="0.4" opacity="0.3" />
        <line x1="40" y1="25" x2="40" y2="95" strokeWidth="0.4" opacity="0.3" />
        <line x1="60" y1="25" x2="60" y2="95" strokeWidth="0.4" opacity="0.3" />
        <line x1="80" y1="25" x2="80" y2="95" strokeWidth="0.4" opacity="0.3" />
        <path d="M 35 50 Q 50 38 75 45 Q 85 60 78 75 Q 60 82 40 75 Q 30 65 35 50 Z" strokeWidth="1.5" />
        <g className="gesture-pencil">
          <line x1="68" y1="58" x2="100" y2="26" strokeWidth="2" />
          <polygon points="100,26 96,30 104,22" fill="currentColor" />
          <line x1="65" y1="61" x2="68" y2="58" strokeWidth="3" />
        </g>
      </svg>
    ),
  },
  {
    num: 'III',
    title: 'La Coupe',
    duration: 'Jour 3',
    text: "Au scalpel, la main glisse sans hésitation. Une erreur de un millimètre condamne la pièce. L'artisan écoute le cuir, suit sa fibre, respecte son rythme. Les découpes tombent comme des pétales.",
    svg: (
      <svg viewBox="0 0 120 120" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.2">
        <path d="M 25 35 L 85 35 L 95 50 L 95 80 L 25 80 Z" strokeWidth="1.2" />
        <line x1="30" y1="55" x2="90" y2="55" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
        <g className="gesture-scalpel">
          <rect x="55" y="20" width="35" height="5" rx="1" transform="rotate(45 72 22)" />
          <polygon points="92,52 84,52 88,46" fill="currentColor" stroke="none" transform="rotate(45 72 22)" />
        </g>
        <path d="M 30 60 L 50 60 L 48 75 L 30 75 Z" strokeWidth="0.8" opacity="0.4" />
        <path d="M 55 60 L 88 60 L 88 75 L 53 75 Z" strokeWidth="0.8" opacity="0.4" />
      </svg>
    ),
  },
  {
    num: 'IV',
    title: 'Le Piquage',
    duration: 'Jours 4 — 8',
    text: "Deux fils cirés, une aiguille à chaque main. Le point sellier s'enroule, se tend, se pose. Huit points au centimètre, ni un de plus, ni un de moins. C'est l'ossature invisible qui rendra la pièce indestructible.",
    svg: (
      <svg viewBox="0 0 120 120" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.2">
        <line x1="20" y1="50" x2="100" y2="50" strokeWidth="2" opacity="0.6" />
        <line x1="20" y1="55" x2="100" y2="55" strokeWidth="2" opacity="0.6" />
        {[25, 35, 45, 55, 65, 75, 85, 95].map((x, i) => (
          <g key={i}>
            <line x1={x} y1="48" x2={x} y2="52" strokeWidth="1.5" className={i < 5 ? 'gesture-stitch-active' : ''} />
            <line x1={x} y1="53" x2={x} y2="57" strokeWidth="1.5" className={i < 5 ? 'gesture-stitch-active' : ''} opacity="0.6" />
          </g>
        ))}
        <g className="gesture-needle">
          <ellipse cx="70" cy="30" rx="2" ry="8" transform="rotate(45 70 30)" />
          <line x1="65" y1="35" x2="78" y2="48" strokeWidth="1.5" transform="rotate(45 70 30)" />
          <path d="M 78 48 Q 75 55 80 60 Q 85 65 90 70" strokeWidth="0.8" strokeDasharray="1 2" />
        </g>
      </svg>
    ),
  },
  {
    num: 'V',
    title: 'Le Teint',
    duration: 'Jours 9 — 11',
    text: "Au pinceau large, la teinte envahit le cuir par couches successives. Quatre à six passes, séchage naturel entre chacune. La main pressent où la couleur doit s'attarder, où elle doit fuir. C'est ici que naît la patine.",
    svg: (
      <svg viewBox="0 0 120 120" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.2">
        <path d="M 25 40 Q 60 30 95 40 L 95 85 Q 60 95 25 85 Z" strokeWidth="1.2" opacity="0.5" />
        <path d="M 35 55 Q 50 50 60 55 Q 70 60 85 55" strokeWidth="0.8" opacity="0.7" />
        <path d="M 35 65 Q 50 60 60 65 Q 70 70 85 65" strokeWidth="0.8" opacity="0.5" />
        <path d="M 35 75 Q 50 70 60 75 Q 70 80 85 75" strokeWidth="0.8" opacity="0.3" />
        <circle cx="40" cy="50" r="2" fill="currentColor" stroke="none" className="gesture-drop" />
        <circle cx="60" cy="60" r="2" fill="currentColor" stroke="none" className="gesture-drop" />
        <circle cx="80" cy="70" r="2" fill="currentColor" stroke="none" className="gesture-drop" />
        <g className="gesture-brush">
          <rect x="58" y="20" width="4" height="22" rx="1" transform="rotate(20 60 30)" />
          <ellipse cx="64" cy="42" rx="4" ry="6" transform="rotate(20 60 30)" />
        </g>
      </svg>
    ),
  },
  {
    num: 'VI',
    title: 'Le Cirage',
    duration: 'Jour 12',
    text: "La cire d'abeille est appliquée au chiffon, en cercles concentriques, jusqu'à ce que le cuir la boive. Puis la pierre d'agate polit la surface. Le grain s'illumine, le toucher devient soyeux. La pièce respire.",
    svg: (
      <svg viewBox="0 0 120 120" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.2">
        <ellipse cx="60" cy="70" rx="40" ry="20" strokeWidth="1.2" opacity="0.5" />
        <ellipse cx="60" cy="70" rx="32" ry="16" strokeWidth="0.8" opacity="0.4" />
        <ellipse cx="60" cy="70" rx="24" ry="12" strokeWidth="0.8" opacity="0.5" />
        <ellipse cx="60" cy="70" rx="16" ry="8" strokeWidth="0.8" opacity="0.6" />
        <ellipse cx="60" cy="70" rx="8" ry="4" strokeWidth="0.8" opacity="0.8" />
        <ellipse cx="55" cy="68" rx="4" ry="2" fill="currentColor" stroke="none" opacity="0.5" className="gesture-shine" />
        <g className="gesture-stone">
          <ellipse cx="50" cy="40" rx="8" ry="5" transform="rotate(-30 50 40)" />
          <ellipse cx="48" cy="38" rx="3" ry="1.5" fill="currentColor" stroke="none" opacity="0.4" transform="rotate(-30 50 40)" />
        </g>
      </svg>
    ),
  },
  {
    num: 'VII',
    title: 'La Signature',
    duration: 'Jour Final',
    text: "L'artisan appose son poinçon, numérote la pièce, calligraphie le certificat à l'encre brune. La cire à cachet scelle l'engagement. La pièce quitte l'atelier non pas comme un produit, mais comme un héritier.",
    svg: (
      <svg viewBox="0 0 120 120" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.2">
        <rect x="25" y="25" width="70" height="50" strokeWidth="1.2" />
        <rect x="30" y="30" width="60" height="40" strokeWidth="0.4" opacity="0.4" />
        <path d="M 38 42 Q 45 38 52 42 Q 55 45 50 48" strokeWidth="0.8" opacity="0.7" />
        <line x1="38" y1="55" x2="75" y2="55" strokeWidth="0.6" opacity="0.5" />
        <line x1="38" y1="60" x2="70" y2="60" strokeWidth="0.6" opacity="0.5" />
        <g className="gesture-seal">
          <circle cx="60" cy="92" r="10" fill="currentColor" stroke="none" opacity="0.9" />
          <circle cx="60" cy="92" r="7" strokeWidth="1" stroke="#F8F1E4" opacity="0.4" />
          <text x="60" y="96" textAnchor="middle" fontSize="8" fill="#F8F1E4" stroke="none" fontWeight="bold">A</text>
        </g>
        <path d="M 55 75 L 55 85 M 65 75 L 65 85" strokeWidth="1" />
      </svg>
    ),
  },
];

export default function SavoirFaire() {
  const { ref, visible } = useReveal();
  const [activeGesture, setActiveGesture] = useState(0);
  const autoRotate = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!visible) return;
    autoRotate.current = setInterval(() => {
      setActiveGesture((i) => (i + 1) % GESTURES.length);
    }, 6000);
    return () => {
      if (autoRotate.current) clearInterval(autoRotate.current);
    };
  }, [visible]);

  const active = GESTURES[activeGesture];

  return (
    <section
      id="savoir-faire"
      ref={ref}
      className="relative py-28 md:py-40 bg-[#1A0E08] overflow-hidden"
    >
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              'radial-gradient(ellipse at 30% 50%, rgba(201, 169, 97, 0.12) 0%, transparent 60%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(248, 241, 228, 0.6) 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-20">
          <div className={`reveal ${visible ? 'visible' : ''} mb-6`}>
            <div className="flex items-center justify-center gap-4">
              <div className="w-10 h-px bg-[#C9A961]" />
              <span className="text-[#C9A961] font-serif-display text-sm tracking-[0.4em] uppercase">
                Le Savoir-Faire
              </span>
              <div className="w-10 h-px bg-[#C9A961]" />
            </div>
          </div>

          <h2
            className={`reveal ${visible ? 'visible' : ''} font-display text-[#F8F1E4] text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.05] mb-8`}
          >
            En Sept Gestes
            <br />
            <span className="italic text-luxe-gold">qui ne trompent pas</span>
          </h2>

          <p
            className={`reveal ${visible ? 'visible' : ''} text-[#F8F1E4]/75 text-lg md:text-xl font-serif-display font-light leading-relaxed`}
          >
            Sept étapes rituelles, sept moments où la main parle et où la machine se
            tait. De la première sélection de peau jusqu'au poinçon final, chaque geste
            obéit à une chorégraphie apprise au fil d'une vie.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-12 lg:gap-20 items-center max-w-7xl mx-auto">
          <div className="space-y-2">
            {GESTURES.map((g, idx) => (
              <button
                key={g.num}
                onClick={() => setActiveGesture(idx)}
                className={`reveal ${visible ? 'visible' : ''} w-full text-left group flex items-center gap-5 p-4 md:p-5 border-l-2 transition-all duration-500 ${
                  idx === activeGesture
                    ? 'border-[#C9A961] bg-[#C9A961]/5'
                    : 'border-[#C9A961]/15 hover:border-[#C9A961]/50 hover:bg-[#2B1810]/30'
                }`}
                style={{ transitionDelay: `${idx * 60}ms` }}
              >
                <div
                  className={`font-display text-3xl md:text-4xl w-12 text-center transition-colors duration-500 ${
                    idx === activeGesture ? 'text-[#C9A961]' : 'text-[#C9A961]/30 group-hover:text-[#C9A961]/60'
                  }`}
                >
                  {g.num}
                </div>
                <div className="flex-1">
                  <div
                    className={`font-display text-lg md:text-xl tracking-wide transition-colors duration-500 ${
                      idx === activeGesture ? 'text-[#F8F1E4]' : 'text-[#F8F1E4]/70 group-hover:text-[#F8F1E4]'
                    }`}
                  >
                    {g.title}
                  </div>
                  <div className="text-[#C9A961]/60 text-[10px] tracking-[0.3em] uppercase font-serif-display mt-1">
                    {g.duration}
                  </div>
                </div>
                <div
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${
                    idx === activeGesture ? 'bg-[#C9A961] scale-150' : 'bg-transparent'
                  }`}
                />
              </button>
            ))}
          </div>

          <div
            key={activeGesture}
            className="relative bg-[#2B1810] border border-[#C9A961]/25 p-8 md:p-12 animate-[cinematic-fade_0.6s_ease-out]"
          >
            <div className="absolute top-3 left-3 w-8 h-8 border-l border-t border-[#C9A961]/60" />
            <div className="absolute top-3 right-3 w-8 h-8 border-r border-t border-[#C9A961]/60" />
            <div className="absolute bottom-3 left-3 w-8 h-8 border-l border-b border-[#C9A961]/60" />
            <div className="absolute bottom-3 right-3 w-8 h-8 border-r border-b border-[#C9A961]/60" />

            <div className="flex flex-col items-center text-center">
              <div className="w-48 h-48 md:w-56 md:h-56 mb-8 text-[#C9A961]">
                {active.svg}
              </div>

              <div className="flex items-center gap-4 mb-4">
                <div className="font-display text-2xl text-[#C9A961]">{active.num}</div>
                <div className="w-12 h-px bg-[#C9A961]/40" />
                <div className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-serif-display">
                  {active.duration}
                </div>
              </div>

              <h3 className="font-display text-3xl md:text-4xl text-[#F8F1E4] mb-5 tracking-wide">
                {active.title}
              </h3>

              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-px bg-[#C9A961]/40" />
                <div className="w-1.5 h-1.5 bg-[#C9A961] rotate-45" />
                <div className="w-8 h-px bg-[#C9A961]/40" />
              </div>

              <p className="text-[#F8F1E4]/75 text-base md:text-lg font-serif-display font-light leading-relaxed max-w-xl">
                {active.text}
              </p>
            </div>

            <div className="flex items-center justify-center gap-1.5 mt-8">
              {GESTURES.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveGesture(idx)}
                  className={`h-px transition-all duration-500 ${
                    idx === activeGesture ? 'w-12 bg-[#C9A961]' : 'w-6 bg-[#C9A961]/25'
                  }`}
                  aria-label={`Geste ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className={`reveal ${visible ? 'visible' : ''} mt-24 max-w-3xl mx-auto text-center`}>
          <div className="divider-gold w-32 mx-auto mb-8" />
          <blockquote className="font-display text-xl md:text-2xl text-[#F8F1E4] italic font-light leading-relaxed mb-4">
            « Le geste juste n'est pas celui qu'on apprend.
            <br />
            <span className="text-luxe-gold">C'est celui qu'on mérite. »</span>
          </blockquote>
          <div className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-serif-display">
            — Maître Artisan, Atelier ASMAO
          </div>
        </div>
      </div>
    </section>
  );
}
